import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { 
  Video, 
  User, 
  ShieldAlert,
  ArrowLeft,
  ArrowRight,
  Monitor,
  Ban,
  Mic,
  Ruler,
  Eye,
  Sun
} from 'lucide-react';

function GuidelinesStep({ consentGiven, setConsentGiven, onBack, onContinue }) {
  return (
    <Card className="max-w-5xl mx-auto">
      <CardContent className="p-4 sm:p-6">
        {/* Three-column grid layout for better horizontal space utilization */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          
          {/* Recording Notice - Column 1 */}
          <div className="p-4 rounded-lg border bg-card">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-violet-500/10 flex items-center justify-center">
                <Video className="w-4 h-4 text-violet-500" />
              </div>
              <h3 className="font-semibold text-lg">Recording Notice</h3>
            </div>
            
            <p className="text-sm text-muted-foreground mb-3">
              This session will be <strong className="text-foreground">recorded</strong> for evaluation:
            </p>
            
            <div className="space-y-3">
              <div className="flex gap-3">
                <div className="w-6 h-6 rounded-full bg-violet-500/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Video className="w-3 h-3 text-violet-500" />
                </div>
                <div>
                  <p className="text-base font-medium">Video</p>
                  <p className="text-sm text-muted-foreground">Your camera feed throughout</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-6 h-6 rounded-full bg-violet-500/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Mic className="w-3 h-3 text-violet-500" />
                </div>
                <div>
                  <p className="text-base font-medium">Audio</p>
                  <p className="text-sm text-muted-foreground">Your microphone input</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-6 h-6 rounded-full bg-violet-500/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Monitor className="w-3 h-3 text-violet-500" />
                </div>
                <div>
                  <p className="text-base font-medium">Screen</p>
                  <p className="text-sm text-muted-foreground">Your screen activity</p>
                </div>
              </div>
            </div>
          </div>

          {/* Camera Positioning - Column 2 */}
          <div className="p-4 rounded-lg border bg-card">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center">
                <User className="w-4 h-4 text-blue-500" />
              </div>
              <h3 className="font-semibold text-lg">Camera & Sitting</h3>
            </div>
            
            <p className="text-sm text-muted-foreground mb-3">
              Position yourself correctly for the interview:
            </p>
            
            <div className="space-y-3">
              <div className="flex gap-3">
                <div className="w-6 h-6 rounded-full bg-blue-500/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Ruler className="w-3 h-3 text-blue-500" />
                </div>
                <div>
                  <p className="text-base font-medium">Proper Distance</p>
                  <p className="text-sm text-muted-foreground">Sit <strong>50-70 cm</strong> from laptop</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-6 h-6 rounded-full bg-blue-500/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Eye className="w-3 h-3 text-blue-500" />
                </div>
                <div>
                  <p className="text-base font-medium">Camera Position</p>
                  <p className="text-sm text-muted-foreground">Keep at <strong>eye level</strong>, stay still</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-6 h-6 rounded-full bg-blue-500/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Sun className="w-3 h-3 text-blue-500" />
                </div>
                <div>
                  <p className="text-base font-medium">Clear Visibility</p>
                  <p className="text-sm text-muted-foreground">Ensure <strong>good lighting</strong> on face</p>
                </div>
              </div>
            </div>
          </div>

          {/* Fair Play Policy - Column 3 */}
          <div className="p-4 rounded-lg border bg-card">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-transparent flex items-center justify-center" style={{ backgroundColor: 'color-mix(in oklch, var(--error) 10%, transparent)' }}>
                <ShieldAlert className="w-4 h-4 text-error" />
              </div>
              <h3 className="font-semibold text-lg">Fair Play Rules</h3>
            </div>
            
            <p className="text-sm text-muted-foreground mb-3">
              The following are <strong className="text-destructive">prohibited</strong>:
            </p>
            
            <div className="space-y-3">
              <div className="flex gap-3">
                <div 
                  className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                  style={{ backgroundColor: 'color-mix(in oklch, var(--destructive) 10%, transparent)' }}
                >
                  <Ban className="w-3 h-3 text-destructive" />
                </div>
                <div>
                  <p className="text-base font-medium">External Help</p>
                  <p className="text-sm text-muted-foreground">No outside resources or people</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div 
                  className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                  style={{ backgroundColor: 'color-mix(in oklch, var(--destructive) 10%, transparent)' }}
                >
                  <Ban className="w-3 h-3 text-destructive" />
                </div>
                <div>
                  <p className="text-base font-medium">Tab Switching</p>
                  <p className="text-sm text-muted-foreground">Stay in the interview window</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div 
                  className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                  style={{ backgroundColor: 'color-mix(in oklch, var(--destructive) 10%, transparent)' }}
                >
                  <Ban className="w-3 h-3 text-destructive" />
                </div>
                <div>
                  <p className="text-base font-medium">Camera Issues</p>
                  <p className="text-sm text-muted-foreground">Don't cover or leave camera</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Consent and Navigation - Full width footer */}
        <div className="pt-4 border-t">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Consent Checkbox */}
            <div className="flex items-start space-x-3 flex-1">
              <Checkbox
                id="consent-checkbox"
                checked={consentGiven}
                onCheckedChange={setConsentGiven}
                className="mt-0.5"
              />
              <Label 
                htmlFor="consent-checkbox" 
                className="text-sm cursor-pointer leading-relaxed"
              >
                I understand and agree to the <strong>recording</strong> and <strong>fair play policies</strong>.
              </Label>
            </div>

            {/* Navigation buttons */}
            <div className="flex gap-2 shrink-0">
              <Button variant="outline" onClick={onBack}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button onClick={onContinue} disabled={!consentGiven}>
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default GuidelinesStep;
